/**
 * LegalDocument — renders a single doc (title + intro + sections). Used by
 * both the public /legal/:slug page AND the members-area Legal Center.
 */
import { Link } from 'react-router-dom';
import { ChevronLeft, Shield, Printer, ExternalLink, FileText } from 'lucide-react';
import { EFFECTIVE_DATE, COMPANY, LEGAL_DOCS } from '@/legal/legalContent';

export default function LegalDocument({ doc, backTo = '/legal', backLabel = 'All legal documents', embedded = false }) {
  if (!doc) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-16 text-center">
        <div className="text-2xl font-bold mb-2">Document not found</div>
        <Link to={backTo} className="text-emerald-600 hover:underline">Back to all documents</Link>
      </div>
    );
  }
  return (
    <div className={embedded ? '' : 'min-h-screen bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200'}>
      <div className="max-w-3xl mx-auto px-5 py-10">
        {/* Back + print */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-600" data-testid="legal-back-link">
            <ChevronLeft className="w-4 h-4" /> {backLabel}
          </Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600" data-testid="legal-print-btn">
            <Printer className="w-3.5 h-3.5" /> Print / Save PDF
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-2">
            {COMPANY.name} · Legal
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid={`legal-title-${doc.slug}`}>{doc.title}</h1>
          <div className="text-xs text-slate-500 mt-2">
            Effective {EFFECTIVE_DATE} · Version 1.0
          </div>
        </div>

        {/* Intro */}
        {doc.intro && (
          <p className="text-base leading-relaxed mb-8 text-slate-700 dark:text-slate-300">{doc.intro}</p>
        )}

        {doc.fullDocument && (
          <a href={doc.fullDocument.href} target="_blank" rel="noopener noreferrer"
             className="mb-8 flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 p-4 text-sm font-semibold text-emerald-700 hover:border-emerald-400 print:hidden"
             data-testid="legal-full-document">
            <FileText className="w-4 h-4 shrink-0" /> {doc.fullDocument.label}
          </a>
        )}

        {/* Sections */}
        <div className="space-y-8">
          {doc.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-lg font-bold mb-2 text-slate-900 dark:text-white">{s.heading}</h2>
              <ul className="space-y-2">
                {s.body.map((line, j) => (
                  <li key={j} className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 pl-4 border-l-2 border-slate-200 dark:border-slate-800">
                    {line}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* Related docs */}
        <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-800 print:hidden">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-3">Related documents</div>
          <div className="flex flex-wrap gap-2">
            {LEGAL_DOCS.filter((d) => d.slug !== doc.slug).map((d) => (
              <Link
                key={d.slug}
                to={`${backTo === '/legal' ? '/legal/' : (backTo + '/')}${d.slug}`}
                className="inline-flex items-center gap-1 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 px-2.5 py-1 rounded-full"
              >
                {d.title} <ExternalLink className="w-3 h-3" />
              </Link>
            ))}
          </div>
        </div>

        {/* Footer notice */}
        <div className="mt-8 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 p-4 text-xs text-slate-700 dark:text-slate-300 flex items-start gap-3">
          <Shield className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            Questions or concerns? Email <a href={`mailto:${COMPANY.legalEmail}`} className="text-emerald-600 font-semibold hover:underline">{COMPANY.legalEmail}</a>{' '}
            or write to {COMPANY.legalName}, {COMPANY.address1}, {COMPANY.address2}.
          </div>
        </div>
      </div>
    </div>
  );
}
